import {Button} from '@orbit-ui/components/button';
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from '@orbit-ui/components/card';
import {createFileRoute, Link} from '@tanstack/react-router';
import {useAuth} from '@workos-inc/authkit-react';
import {LoaderCircleIcon} from 'lucide-react';
import {PageContent} from '@/components/page-content';
import {PageWrapper} from '@/components/page-wrapper';
import {usePermissions} from '@/features/auth/hooks/use-permissions';
import {useUserInfo} from '@/features/auth/hooks/use-user-info';
import {UserInfo} from '@/features/auth/types/user';
import {ApiError} from '@/features/core/api/api-errors';

// Define the route
export const Route = createFileRoute('/_app/home')({
  component: HomePage,
  async beforeLoad() {
    return {
      title: 'Home',
      breadcrumb: {
        label: 'Home',
        path: '/',
      },
    };
  },
});

function HomePage() {
  const {user} = useAuth();
  const {userInfo, isLoading: userInfoLoading, error: userInfoError} = useUserInfo();
  const {isSsoAdmin} = usePermissions();

  if (userInfoLoading) {
    return <LoadingView />;
  }

  if (userInfoError) {
    return <ErrorView error={userInfoError} />;
  }

  return (
    <PageWrapper>
      <PageContent className="p-6">
        <h1 className="mb-2 text-2xl font-bold">Welcome to Cosmonic Cloud</h1>

        {isSsoAdmin() && userInfo ? (
          <AdminView userInfo={userInfo} currentUserEmail={user?.email} />
        ) : (
          <MemberView email={user?.email} adminInfo={userInfo?.admin} />
        )}
      </PageContent>
    </PageWrapper>
  );
}

function LoadingView() {
  return (
    <PageWrapper>
      <PageContent className="p-6">
        <div className="flex h-64 items-center justify-center">
          <LoaderCircleIcon className="mr-2 size-8 animate-spin" />
          <span>a few moments later...</span>
        </div>
      </PageContent>
    </PageWrapper>
  );
}

function ErrorView({error}: {error: Error}) {
  // Get a user-friendly message if it's an ApiError
  const errorMessage = error instanceof ApiError ? error.getUserFriendlyMessage() : error.message;

  return (
    <PageWrapper>
      <PageContent className="p-6">
        <div className="text-red-500">
          <h1 className="mb-2 text-2xl font-bold">Error</h1>
          <p>{errorMessage}</p>
          <Button variant="outline" className="mt-4" onClick={() => globalThis.location.reload()}>
            Retry
          </Button>
        </div>
      </PageContent>
    </PageWrapper>
  );
}

function AdminView({userInfo, currentUserEmail}: {userInfo: UserInfo; currentUserEmail?: string}) {
  const isCurrentUserAdmin = currentUserEmail && userInfo.admin?.email === currentUserEmail;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Organization Admin</CardTitle>
          <CardDescription>
            {isCurrentUserAdmin
              ? 'You are the administrator for your organization'
              : 'Organization administrator information'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isCurrentUserAdmin && userInfo.organization_domain ? (
            <CurrentAdminContent orgDomain={userInfo.organization_domain} />
          ) : userInfo.admin ? (
            <AdminInfoContent
              adminName={userInfo.admin.name}
              adminEmail={userInfo.admin.email}
              adminProfilePictureUrl={userInfo.admin.profile_picture_url}
            />
          ) : (
            <p>No administrator information available.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function CurrentAdminContent({orgDomain}: {orgDomain: string}) {
  return (
    <>
      <p className="mb-4">As the administrator for your organization ({orgDomain}), you can:</p>
      <ul className="mb-4 list-disc space-y-2 pl-5">
        <li>Configure Single Sign-On (SSO) for your organization</li>
        <li>Manage users and their permissions</li>
        <li>Set up and manage clusters</li>
      </ul>
      <div className="mt-6 flex space-x-4">
        <Button asChild>
          <Link to="/admin/sso">Configure SSO</Link>
        </Button>
        <Button asChild>
          <Link to="/apikeys">Manage API Keys</Link>
        </Button>
        <Button variant="outline" asChild>
          <Link to="/clusters">Manage Clusters</Link>
        </Button>
      </div>
    </>
  );
}

function AdminInfoContent({
  adminName,
  adminEmail,
  adminProfilePictureUrl,
}: {
  adminName: string;
  adminEmail: string;
  adminProfilePictureUrl: string;
}) {
  return (
    <div className="mb-4 rounded-md bg-gray-50 p-4">
      <h3 className="mb-2 font-medium">Organization Administrator</h3>
      <div className="flex items-center">
        {adminProfilePictureUrl && (
          <img src={adminProfilePictureUrl} alt={adminName} className="mr-4 size-12 rounded-full" />
        )}
        <div>
          <p>
            The administrator for your organization is: <strong>{adminName}</strong> ({adminEmail})
          </p>
          <p className="mt-2 text-sm text-gray-600">
            Contact your administrator if you need help with access or permissions.
          </p>
        </div>
      </div>
    </div>
  );
}

function MemberView({
  email,
  adminInfo,
}: {
  email?: string;
  adminInfo?: {
    name: string;
    email: string;
    profile_picture_url: string;
  } | null;
}) {
  if (!email || !adminInfo) return null;

  const orgDomain = email.split('@')[1];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Organization Member</CardTitle>
          <CardDescription>You are a member of the {orgDomain} organization</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="mb-4">
            Welcome to Cosmonic Cloud! You have been added as a member of your organization.
          </p>
          <AdminInfoContent
            adminName={adminInfo.name}
            adminEmail={adminInfo.email}
            adminProfilePictureUrl={adminInfo.profile_picture_url}
          />
          <div className="mt-6">
            <Button asChild>
              <Link to="/clusters">View Clusters</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
